import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { User, MapPin, Car, Truck, CheckCircle } from "lucide-react";

// Helper function to format date and time in 24-hour format
const formatDateTime24 = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const getStatusBadge = (status) => {
  const styles = {
    pending: "status-pending",
    confirmed: "status-confirmed",
    dispatched: "status-dispatched",
    in_progress: "status-in_progress",
    completed: "status-completed",
    cancelled: "status-cancelled"
  };
  return <Badge className={`${styles[status]} capitalize`}>{status?.replace("_", " ")}</Badge>;
};

const getPaymentBadge = (status) => {
  const styles = {
    pending: "payment-pending",
    paid: "payment-paid",
    failed: "payment-failed"
  };
  return <Badge className={`${styles[status]} capitalize`}>{status}</Badge>;
};

export const BookingCard = ({
  booking,
  isSelected,
  onSelect,
  onConfirm,
  onDispatch,
  drivers
}) => {
  const navigate = useNavigate();

  return (
    <Card 
      className={`dashboard-card overflow-hidden ${isSelected ? 'ring-2 ring-primary' : ''}`}
      onClick={() => navigate(`/booking/${booking.id}`)}
    >
      <CardContent className="p-0">
        {/* Status bar at top */}
        <div className={`h-1 ${
          booking.status === 'completed' ? 'bg-gray-500' :
          booking.status === 'dispatched' ? 'bg-purple-500' :
          booking.status === 'confirmed' ? 'bg-blue-500' :
          booking.status === 'cancelled' ? 'bg-red-500' :
          'bg-yellow-500'
        }`} />
        
        <div className="p-4 space-y-3">
          {/* Header row */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={isSelected}
                onCheckedChange={(checked) => onSelect(booking.id, checked)}
                onClick={(e) => e.stopPropagation()}
                data-testid={`mobile-select-${booking.id}`}
              />
              <div>
                <p className="font-mono text-primary font-semibold text-sm">{booking.booking_ref}</p>
                <p className="text-xs text-muted-foreground">{formatDateTime24(booking.pickup_datetime)}</p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              {getStatusBadge(booking.status)}
              {getPaymentBadge(booking.payment_status)}
            </div>
          </div>

          {/* Passenger */}
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-primary flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-medium truncate">{booking.passenger_name}</p>
              <p className="text-xs text-muted-foreground">{booking.passenger_phone}</p>
            </div>
          </div>

          {/* Route */}
          <div className="space-y-1 text-sm">
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
              <p className="truncate flex-1">{booking.pickup_location}</p>
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="truncate flex-1">{booking.dropoff_location || 'TBD'}</p>
            </div>
          </div>

          {/* Vehicle & Driver */}
          <div className="flex items-center justify-between text-sm pt-2 border-t border-white/5">
            <div className="flex items-center gap-2">
              <Car className="h-4 w-4 text-muted-foreground" />
              <span className="capitalize">{booking.vehicle_type}</span>
            </div>
            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 text-muted-foreground" />
              <span>{booking.driver_name || <span className="text-muted-foreground">Unassigned</span>}</span>
            </div>
          </div>

          {/* Quick Actions */}
          {(booking.status === "pending" || (booking.status === "confirmed" && !booking.driver_id)) && (
            <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
              {booking.status === "pending" && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => onConfirm(booking.id)}
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Confirm
                </Button>
              )}
              {!booking.driver_id && drivers.length > 0 && (
                <Select onValueChange={(driverId) => onDispatch(booking.id, driverId)}>
                  <SelectTrigger className="flex-1 h-9">
                    <Truck className="h-4 w-4 mr-1" />
                    <SelectValue placeholder="Dispatch" />
                  </SelectTrigger>
                  <SelectContent>
                    {drivers.map(driver => (
                      <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BookingCard;
